/**
 * Polskie święta państwowe i dni ustawowo wolne od pracy.
 * Używane do obliczania dni urlopowych (wykluczamy je z puli).
 * Źródło: Ustawa z dnia 18 stycznia 1951 r. o dniach wolnych od pracy
 */

/**
 * Oblicza datę Wielkanocy (algorytm Meeusa/Jonesa/Butchera)
 * @param {number} year
 * @returns {Date} Niedziela wielkanocna
 */
function getEasterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * Dodaje dni do daty
 */
function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Zwraca Set dat świąt w formacie "YYYY-MM-DD" dla podanych lat
 * @param {number[]} years - np. [2024, 2025, 2026]
 * @returns {Set<string>}
 */
export function getPolishHolidaysSet(years) {
  const set = new Set();
  const fixedHolidays = [
    [1, 1],   // Nowy Rok
    [1, 6],   // Święto Trzech Króli
    [5, 1],   // Święto Pracy
    [5, 3],   // Święto Konstytucji 3 Maja
    [8, 15],  // Wniebowzięcie NMP
    [11, 1],  // Wszystkich Świętych
    [11, 11], // Narodowe Święto Niepodległości
    [12, 25], // Boże Narodzenie (pierwszy dzień)
    [12, 26], // Boże Narodzenie (drugi dzień)
  ];

  for (const year of years) {
    // Święta stałe
    for (const [month, day] of fixedHolidays) {
      set.add(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    }
    // Wigilia (często dzień wolny w praktyce)
    set.add(`${year}-12-24`);

    // Święta ruchome
    const easter = getEasterSunday(year);
    set.add(formatDate(easter));                           // Wielkanoc (niedziela)
    set.add(formatDate(addDays(easter, 1)));               // Poniedziałek wielkanocny
    set.add(formatDate(addDays(easter, 60)));              // Boże Ciało
    const pentecost = addDays(easter, 49);                 // Zielone Świątki (7 tygodni)
    set.add(formatDate(pentecost));
  }

  return set;
}

function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Liczy dni robocze (bez weekendów i świąt) w podanym zakresie
 * @param {string} dataOd - YYYY-MM-DD
 * @param {string} dataDo - YYYY-MM-DD
 * @returns {number}
 */
export function countWorkingDays(dataOd, dataDo) {
  const [y1, m1, d1] = dataOd.split('-').map(Number);
  const [y2, m2, d2] = dataDo.split('-').map(Number);
  const start = new Date(y1, m1 - 1, d1);
  const end = new Date(y2, m2 - 1, d2);
  if (start > end) return 0;

  const years = [...new Set([start.getFullYear(), end.getFullYear()])];
  const holidays = getPolishHolidaysSet(years);

  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const dayOfWeek = current.getDay();
    const dateStr = formatDate(current);
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = holidays.has(dateStr);
    if (!isWeekend && !isHoliday) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

/**
 * Wszystkie dni kalendarzowe w zakresie (od–do włącznie): weekendy i święta też się liczą.
 * Dla umów z tygodniem pracy 7-dniowym / obowiązkiem w weekendy i święta.
 * @param {string} dataOd - YYYY-MM-DD
 * @param {string} dataDo - YYYY-MM-DD
 * @returns {number}
 */
export function countCalendarDays(dataOd, dataDo) {
  const [y1, m1, d1] = dataOd.split('-').map(Number);
  const [y2, m2, d2] = dataDo.split('-').map(Number);
  const start = new Date(y1, m1 - 1, d1);
  const end = new Date(y2, m2 - 1, d2);
  if (start > end) return 0;
  const msPerDay = 86400000;
  return Math.round((end.getTime() - start.getTime()) / msPerDay) + 1;
}
